/**
 * Axios Plugin implementing secured and plain axios requests
 */

import axios from 'axios';
import { processError } from '@/api/errorHandler';
import router from '../router';
import * as Sentry from '@sentry/vue';
import { useSessionStore } from '@/store';
import { setUser } from '@/plugins/sentry';
import axiosRetry from 'axios-retry';

const BASE_HOST = import.meta.env.VITE_BASE_HOST;

console.log('BASE_HOST', BASE_HOST);

const TENANT_SLUG = computed(() => {
    const baseHostReplaceable = '\\.' + BASE_HOST.replace('.', '\\.');
    return window.location.hostname.replace(new RegExp(`${baseHostReplaceable}$`), '');
});

console.log('TENANT_SLUG', TENANT_SLUG.value);

console.log('BASE_SCHEME', window.location.protocol);

const BASE_API_URL = computed(() => {
    return `${import.meta.env.VITE_API_BASE_URL}/${TENANT_SLUG.value}/api/v1`;
});

console.log('BASE_API_URL', BASE_API_URL.value);

const axiosInstance = axios.create({
    baseURL: BASE_API_URL.value,
    headers: {
        'X-Request-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Max-Age': '0',
        'Access-Control-Allow-Credentials': true,
    },
    withCredentials: true,
    withXSRFToken: true,
});

axiosRetry(axiosInstance, { retries: 3 });

const noAuthAxios = axios.create({
    baseURL: BASE_API_URL.value,
    headers: {
        'X-Request-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    },
    withCredentials: false,
});

axiosInstance.interceptors.request.use((config) => {
    const session = useSessionStore();
    if (session.bearer) {
        config.headers['Authorization'] = `Bearer ${session.bearer}`;
    }
    return config;
});

/**
 * Marker set on the plain object returned by `processError` so nested retry
 * re-entries can be recognised without swallowing genuinely non-Axios rejections.
 */
const PROCESSED_ERROR_MARKER = '__bookProcessedError';

/**
 * Response error interceptor for the authenticated book axios instance.
 *
 * axios-retry retries by calling `axiosInstance(config)`, which builds a nested
 * interceptor chain. This handler transforms Axios errors into plain objects via
 * `processError` before rejecting. Without a guard, each outer chain level
 * re-enters here with that plain object and re-reports to Sentry as
 * `Axios Response on <route>: null (undefined from undefined undefined)`
 * (MOVEPRO-BOOK-AB / MOVEPRO-BOOK-1E).
 *
 * We gate on our own `PROCESSED_ERROR_MARKER` rather than `!axios.isAxiosError`
 * so a genuinely non-Axios rejection (e.g. a throw in the request interceptor)
 * still flows through `processError` — keeping its `{ code, message, errors }`
 * shape for callers like `FillForm.vue` and a Sentry breadcrumb — instead of
 * being rejected raw.
 *
 * Exported for unit tests only.
 */
export async function axiosResponseErrorInterceptor(error) {
    // Already-processed rejection re-entering from a nested retry chain.
    if (error?.[PROCESSED_ERROR_MARKER]) {
        return Promise.reject(error);
    }

    // Client transport noise — same policy as CRM `isNoiseAxiosError` (CU-86d3vb6hq).
    // Still redirect ERR_NETWORK → ServerError below; just don't open a Sentry issue.
    const isNetworkNoise =
        error.code === 'ERR_CANCELED' || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

    if (
        !isNetworkNoise &&
        (import.meta.env.VITE_ENV == 'production' ||
            import.meta.env.VITE_ENV == 'testing' ||
            import.meta.env.VITE_ENV == 'development')
    ) {
        const session = useSessionStore();

        setUser({ orderRef: session.orderRef });

        Sentry.withScope((scope) => {
            const currentRoute = router?.currentRoute?.value;

            scope.setLevel(error?.response?.status == 422 ? 'warning' : 'error');

            scope.setExtras({
                error: error,
                code: error?.code,

                request_method: error?.config?.method,
                request_url: error?.config?.url,

                response_data: error?.response?.data,
                response_status: error?.response?.status,
                response_status_text: error?.response?.statusText,
                response_headers: error?.response?.headers,

                current_route_fullPath: currentRoute?.fullPath,
                current_route_name: currentRoute?.name,
                current_route_path: currentRoute?.path,
                current_route_query: currentRoute?.query,
                current_route_params: currentRoute?.params,
                current_route_hash: currentRoute?.hash,
            });

            Sentry.captureException(
                new Error(
                    'Axios Response on ' +
                        (currentRoute?.name ?? 'undefined route') +
                        ': ' +
                        error?.code +
                        ' (' +
                        error?.response?.status +
                        ' from ' +
                        error?.config?.method?.toUpperCase() +
                        ' ' +
                        error?.config?.url +
                        ')',
                ),
            );
        });
    }

    if (error?.response?.status === 503) {
        if (router.currentRoute.value.name !== 'Maintenance') {
            router.replace({ name: 'Maintenance' });
        }
    } else if (error?.code === 'ERR_NETWORK') {
        if (router.currentRoute.value.name !== 'ServerError') {
            router.push({ name: 'ServerError' });
        }
    } else if (error?.response?.status === 403) {
        if (router.currentRoute.value.name !== 'SessionForbidden') {
            router.replace({ name: 'SessionForbidden' });
        }
    } else if (error?.response?.status === 401) {
        if (router.currentRoute.value.name !== 'SessionExpired') {
            router.push({ name: 'SessionExpired' });
        }
    }

    let processedError = processError(error);
    processedError[PROCESSED_ERROR_MARKER] = true;

    console.log('Processed Error', processedError);

    return Promise.reject(processedError);
}

axiosInstance.interceptors.response.use(null, axiosResponseErrorInterceptor);

/*
 * Install global and Vue alias
 */
window.axios = axiosInstance; // Make instance available globally as axios()
window.noAuthAxios = noAuthAxios;

export default {
    install: (app /*options*/) => {
        app.config.globalProperties.$axios = axiosInstance; // Make instance available as instance property this.$axios()
    },
};
